import React, { useState, useEffect, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { 
    ArrowLeft, 
    Monitor, 
    Plus, 
    Trash2, 
    Calendar, 
    Hash, 
    Settings,
    AlertTriangle,
    CheckCircle,
    RefreshCw,
    Search
} from 'lucide-react';

import { getVendingMachineRegisterRequests, registerVendingMachine,
     deleteVendingMachineRequest } from '../../actions/vendingMachine';

const RegisterVM = ({ getVendingMachineRegisterRequests, deleteVendingMachineRequest, registrationList, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isDeleting, setIsDeleting] = useState({});
    const navigate = useNavigate();
    useEffect(() => {
        document.title = 'Register Vending Machine';
        if(registrationList === null){
            getVendingMachineRegisterRequests();
        }
    }, [ registrationList ] );

    const deleteRequest = async (e, serialNumber) => {
        setIsDeleting(prev => ({ ...prev, [serialNumber]: true }));
        try {
            await deleteVendingMachineRequest({"serialNumber": serialNumber});
        } finally {
            setIsDeleting(prev => ({ ...prev, [serialNumber]: false }));
        }
    }

    const filteredRequests = useMemo(() => {
        if (!registrationList || !Array.isArray(registrationList)) return [];
        
        return registrationList.filter(obj =>
            obj.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            obj.version?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [registrationList, searchTerm]);

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const populateAllRequest = () => {
        if (!filteredRequests.length) {
            return (
                <tr>
                    <td colSpan="5" className="text-center py-5">
                        <Monitor size={48} className="text-muted mb-3" />
                        <p className="text-muted mb-0">
                            {searchTerm ? 'No requests match your search criteria' : 'No registration requests found'}
                        </p>
                    </td>
                </tr>
            );
        }

        return filteredRequests.map((obj, index) => (
            <tr key={index} className="align-middle">
                <td className="fw-bold text-primary">
                    <Hash size={16} className="me-2" />
                    {obj.serialNumber}
                </td>
                <td>
                    <span className="badge bg-info fs-6">
                        v{obj.version}
                    </span>
                </td>
                <td className="text-muted">
                    <Calendar size={16} className="me-2" />
                    {formatDate(obj.dateTime)}
                </td>
                <td>
                    <Link 
                        className="btn btn-success btn-sm d-flex align-items-center w-fit"
                        to="/inventory/v1.2/list-registered-vending-machine/new"
                        state={{ serialNumber: obj.serialNumber, version: String(obj.version) }}
                    >
                        <Plus size={16} className="me-2" />
                        Register
                    </Link>
                </td>
                <td>
                    <button 
                        className="btn btn-danger btn-sm d-flex align-items-center"
                        onClick={e => deleteRequest(e, obj.serialNumber)}
                        disabled={isDeleting[obj.serialNumber]}
                    >
                        {isDeleting[obj.serialNumber] ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 size={16} className="me-2" />
                                Delete
                            </>
                        )}
                    </button>
                </td>
            </tr>
        ));
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <Link className="btn btn-outline-primary d-flex align-items-center mb-2" to="/">
                                    <ArrowLeft size={16} className="me-2" />
                                    Back to Dashboard
                                </Link>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <Monitor size={32} className="me-3 text-primary" />
                                    Register Vending Machine
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage pending vending machine registration requests
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {loading && (
                                    <div className="d-flex align-items-center text-muted">
                                        <RefreshCw size={16} className="me-2 spinner-border spinner-border-sm" />
                                        Loading...
                                    </div>
                                )}
                                <span className="badge bg-primary fs-6">
                                    {filteredRequests.length} Request{filteredRequests.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search Section */}
                <div className="row mb-4">
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search by serial number or version..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Registration Requests Table */}
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-white border-bottom">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Settings size={20} className="me-2 text-primary" />
                                        Registration Requests
                                        {filteredRequests.length !== registrationList?.length && (
                                            <span className="badge bg-info ms-2">
                                                {filteredRequests.length} of {registrationList?.length}
                                            </span>
                                        )}
                                    </h5>
                                    {filteredRequests.length > 0 && (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-success d-flex align-items-center">
                                                <CheckCircle size={12} className="me-1" />
                                                Ready to Register
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="border-0 fw-bold">
                                                    <Hash size={16} className="me-2" />
                                                    Serial Number
                                                </th>
                                                <th className="border-0 fw-bold">
                                                    <Settings size={16} className="me-2" />
                                                    Version
                                                </th>
                                                <th className="border-0 fw-bold">
                                                    <Calendar size={16} className="me-2" />
                                                    Request Date
                                                </th>
                                                <th className="border-0 fw-bold text-center">Action</th>
                                                <th className="border-0 fw-bold text-center">Remove</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {populateAllRequest()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {filteredRequests.length === 0 && !loading && (
                                <div className="card-footer bg-light text-center">
                                    <small className="text-muted">
                                        {searchTerm ? 
                                            'Try adjusting your search criteria to find more requests.' :
                                            'New registration requests will appear here when machines request registration.'
                                        }
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

RegisterVM.propTypes = {
    getVendingMachineRegisterRequests: PropTypes.func.isRequired,
    registerVendingMachine: PropTypes.func.isRequired,
    deleteVendingMachineRequest: PropTypes.func.isRequired,
    registrationList: PropTypes.array,
    loading: PropTypes.bool
}

const mapStateToProps = state => ({
    registrationList: state.vendingMachine.registrationList,
    loading: state.vendingMachine.loading
});

export default connect( mapStateToProps, { getVendingMachineRegisterRequests,
     registerVendingMachine, deleteVendingMachineRequest } )( RegisterVM );